/**
 * Mapa de símbolos por código de moneda
 */
export const CURRENCY_SYMBOLS = {
  USD: "$", EUR: "€", GBP: "£", JPY: "¥",
  CHF: "Fr", CAD: "C$", AUD: "A$", NZD: "NZ$",
  INR: "₹", BRL: "R$", MXN: "$", SGD: "S$",
  HKD: "HK$", SEK: "kr", NOK: "kr", DKK: "kr",
  ZAR: "R", TWD: "NT$", VND: "₫", THB: "฿",
  MYR: "RM", PHP: "₱", IDR: "Rp", KRW: "₩",
  RUB: "₽", TRY: "₺", AED: "د.إ", SAR: "﷼",
  CLP: "$", COP: "$", ARS: "$", BGN: "лв",
  CZK: "Kč", HRK: "kn", HUF: "Ft", PLN: "zł",
  RON: "lei", ILS: "₪", EGP: "£", CRC: "₡",
  UYU: "$", VEF: "Bs", UZS: "so'm", QAR: "ر.ق",
};

/**
 * Devuelve el símbolo de una moneda dado su código.
 * Ejemplo: currencySymbol("MXN") → "$"
 */
export function currencySymbol(currency) {
  return CURRENCY_SYMBOLS[currency] || currency;
}

/**
 * Convierte un precio desde cualquier moneda a la moneda del usuario.
 *
 * Versión simple (para uso genérico):
 *   convertPrice(100, "MXN", rates) → número convertido
 *
 * Versión App (con perfil de usuario):
 *   convertPriceForUser(amount, fromCurrency, userProfile, exchangeRates)
 *   → { val: número convertido, sym: símbolo } o null si el monto no es válido
 *
 * En App.js reemplaza la función convertPrice actual por convertPriceForUser.
 */

/**
 * Uso genérico: convierte amount desde currency usando rates como base.
 */
export function convertPrice(amount, currency, rates) {
  if (!rates || !currency || !amount) return 0;
  const rate = rates[currency];
  if (!rate) return amount;
  return amount * rate;
}

/**
 * Uso en App.js: convierte al currency preferido del usuario.
 * Devuelve { val, sym } o null si el monto no es válido.
 *
 * Ejemplo:
 *   const result = convertPriceForUser("25.00", "USD", userProfile, exchangeRates);
 *   // → { val: 487.5, sym: "$" }  (si el usuario usa MXN)
 */
export function convertPriceForUser(amount, fromCurrency, userProfile, exchangeRates) {
  const toCurrency = userProfile?.currency || "USD";

  // Si el monto no es un número válido, no podemos convertir
  if (!amount || isNaN(parseFloat(amount))) return null;

  const val = parseFloat(amount);

  // Si ya está en la moneda correcta o no hay tasas, devolvemos directo
  if (!exchangeRates || fromCurrency === toCurrency) {
    return { val, sym: currencySymbol(toCurrency) };
  }

  // Convertimos usando la tasa base (la API devuelve tasas relativas a toCurrency)
  const rate = exchangeRates.rates[fromCurrency];
  if (!rate) return { val, sym: currencySymbol(fromCurrency) };

  return { val: val / rate, sym: currencySymbol(toCurrency) };
}

/**
 * Detecta el país del usuario según el idioma del navegador.
 * Devuelve un código de país de 2 letras (ej: "MX", "FI", "US").
 */
export function detectCountry() {
  const nav = (navigator.language || navigator.userLanguage || "en-US").toLowerCase();

  if (nav.startsWith("es")) {
    if (nav.includes("mx")) return "MX";
    if (nav.includes("ar")) return "AR";
    if (nav.includes("co")) return "CO";
    if (nav.includes("cl")) return "CL";
    if (nav.includes("pe")) return "PE";
    if (nav.includes("ve")) return "VE";
    return "MX";
  }

  if (nav.startsWith("pt-br")) return "BR";
  if (nav.startsWith("pt"))    return "PT";
  if (nav.startsWith("en-gb")) return "GB";
  if (nav.startsWith("en"))    return "US";
  if (nav.startsWith("fr"))    return "FR";
  if (nav.startsWith("de"))    return "DE";
  if (nav.startsWith("it"))    return "IT";
  if (nav.startsWith("ja"))    return "JP";
  if (nav.startsWith("zh"))    return "CN";
  if (nav.startsWith("ru"))    return "RU";
  if (nav.startsWith("sv"))    return "SE";
  if (nav.startsWith("no"))    return "NO";
  if (nav.startsWith("da"))    return "DK";
  if (nav.startsWith("fi"))    return "FI";

  return "US";
}
